import { useMemo, type CSSProperties } from 'react';
import styles from '../Disco.module.css';
import {
  ScreensRenderer,
  offerRenderers,
  parseScreens,
  type Screen,
} from '../offerRenderer';
import type { DiscoOffer } from '../types';

interface AdContentProps {
  subheading: string;
  disclosureDetails?: DiscoOffer['disclosure_details'];
  jsonContent?: unknown;
  bodyStyle?: CSSProperties;
}

const BACK_COPY = 'Back';
const DISCLOSURE_TERMS_MAX_BLOCK_SIZE = 200;

function buildDisclosureScreens(
  subheading: string,
  copy: string,
  content: string,
): Screen[] {
  return [
    {
      id: 'default',
      children: [
        {
          type: 'paragraph',
          children: [
            { type: 'text', text: `${subheading} ` },
            {
              type: 'link',
              attributes: { targetScreenId: 'terms' },
              children: [{ type: 'text', text: copy }],
            },
          ],
        },
      ],
    },
    {
      id: 'terms',
      children: [
        {
          type: 'scrollview',
          attributes: { maxBlockSize: DISCLOSURE_TERMS_MAX_BLOCK_SIZE },
          children: [
            {
              type: 'paragraph',
              children: [{ type: 'text', text: content }],
            },
          ],
        },
        {
          type: 'paragraph',
          children: [
            {
              type: 'link',
              attributes: { targetScreenId: 'default' },
              children: [{ type: 'text', text: BACK_COPY }],
            },
          ],
        },
      ],
    },
  ];
}

export function AdContent({
  subheading,
  disclosureDetails,
  jsonContent,
  bodyStyle,
}: AdContentProps) {
  const disclosureCopy = disclosureDetails?.copy;
  const disclosureContent = disclosureDetails?.content;

  const screens = useMemo<Screen[] | null>(() => {
    const parsed = parseScreens(jsonContent);
    if (parsed) return parsed;

    if (disclosureCopy && disclosureContent) {
      return buildDisclosureScreens(
        subheading,
        disclosureCopy,
        disclosureContent,
      );
    }

    return null;
  }, [jsonContent, subheading, disclosureCopy, disclosureContent]);

  return (
    <div className={styles.subheading} style={bodyStyle}>
      {screens ? (
        <ScreensRenderer screens={screens} renderers={offerRenderers} />
      ) : (
        <>
          {subheading}
          {disclosureDetails && (
            <>
              {' '}
              <a
                href={disclosureDetails.target_url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.disclosureLink}
              >
                {disclosureDetails.copy}
              </a>
            </>
          )}
        </>
      )}
    </div>
  );
}
